import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Image, Palette, Globe } from 'lucide-react'
import { ImageGallery } from '../../common/ImageGallery'

export const BaresSitePersonalization: React.FC = () => {
  const { t } = useTranslation('bares')

  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const images = [
    { src: '/images/bares/01-home-site-desktop.png', alt: 'Site - Página inicial', title: 'Site - Página inicial', description: 'Sua identidade visual aplicada no site.' },
    { src: '/images/bares/02-site-cardapio-desktop.png', alt: 'Site - Cardápio', title: 'Site - Cardápio', description: 'Cardápio com fotos e categorias.' }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 right-10 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-orange-300/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
          >
            <Globe className="w-4 h-4" />
            {t('site.kicker', 'Sua Marca Online')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {t('site.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('site.subtitle')}
          </motion.p>
        </div>

        {/* Site Screenshots */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {images.map((img, i) => (
            <motion.button
              key={img.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-2xl border-2 border-amber-200 p-6 shadow-xl hover:shadow-2xl hover:border-amber-300 transition-all duration-300 hover:-translate-y-2"
              onClick={() => { setIndex(i); setOpen(true) }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full rounded-xl border-2 border-gray-100 shadow-md group-hover:scale-[1.02] transition-transform duration-300"
              />
              <div className="mt-4 font-semibold text-gray-700 group-hover:text-amber-600 transition-colors">{img.title}</div>
            </motion.button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Image className="w-6 h-6 text-amber-600" />
              </div>
              <strong className="text-xl text-gray-900">{t('site.media.title')}</strong>
            </div>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span className="text-lg">{t('site.media.items.photos')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span className="text-lg">{t('site.media.items.logo')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span className="text-lg">{t('site.media.items.domains')}</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl border-2 border-orange-200 p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Palette className="w-6 h-6 text-orange-600" />
              </div>
              <strong className="text-xl text-gray-900">{t('site.branding.title')}</strong>
            </div>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <span className="text-lg">{t('site.branding.items.colors')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <span className="text-lg">{t('site.branding.items.typography')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <span className="text-lg">{t('site.branding.items.layout')}</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
      <ImageGallery images={images} isOpen={open} onClose={() => setOpen(false)} initialIndex={index} />
    </section>
  )
}
